import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import WorkCard from "@/components/base/WorkCard";
import { getHotTags, searchWorks } from "@/utils/gallery";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("query") || "";
  const [input, setInput] = useState(query);

  useEffect(() => {
    setInput(query);
    document.title = query
      ? `“${query}”的搜索结果 - 国模白露写真作品集`
      : "搜索作品 - 国模白露写真作品集";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        `在国模白露搜索“${query}”相关的模特写真摄影作品，按标题、模特姓名与描述关键词模糊匹配。`
      );
    }
  }, [query]);

  const results = useMemo(() => searchWorks(query), [query]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next = input.trim();
    if (!next) return;
    setSearchParams({ query: next });
  };

  return (
    <div className="px-4 pb-20 pt-24 md:px-8 md:pb-28 md:pt-28">
      <div className="mb-6">
        <Breadcrumb items={[{ label: "搜索结果" }]} />
      </div>

      <h1 className="text-2xl font-bold text-foreground-950 md:text-3xl">
        {query ? (
          <>
            搜索 “<span className="text-primary-600">{query}</span>”
          </>
        ) : (
          "搜索写真作品"
        )}
      </h1>

      <form onSubmit={handleSubmit} className="mt-6 flex max-w-xl items-center gap-3">
        <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-3 focus-within:border-primary-400">
          <i className="ri-search-line text-foreground-500" />
          <input
            type="text"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="输入作品标题、模特姓名或关键词"
            className="flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
          />
        </div>
        <button
          type="submit"
          className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          搜索
        </button>
      </form>

      {query && (
        <p className="mt-6 text-sm text-foreground-600">
          共找到 <span className="font-semibold text-foreground-950">{results.length}</span> 组相关作品
        </p>
      )}

      {results.length > 0 ? (
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {results.map((work, index) => (
            <WorkCard key={work.id} work={work} index={index} />
          ))}
        </div>
      ) : (
        <div className="mt-8 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-16 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-search-eye-line text-3xl" />
          </div>
          <h2 className="mt-5 text-base font-semibold text-foreground-950">
            {query ? "没有找到相关作品" : "输入关键词开始搜索"}
          </h2>
          <p className="mt-2 max-w-md text-sm text-foreground-500">
            {query
              ? "换个关键词试试，或从下面的热门搜索中选择你感兴趣的主题。"
              : "你可以搜索作品标题、模特姓名、摄影师或作品描述中的关键词。"}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            {getHotTags().map((tag) => (
              <Link
                key={tag}
                to={`/search?query=${encodeURIComponent(tag)}`}
                className="whitespace-nowrap rounded-full border border-background-300 px-4 py-1.5 text-sm text-foreground-600 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                {tag}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}